/** @graphql experimental **/

import gql from 'graphql-tag';
import { useCallback } from 'react';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { PlatformSiteAri } from '@atlassian/ari';

import { useLazyQueryInterop } from '@confluence/apollo-relay-interop';

import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller';
import { useExperimentalInvitePermissionsQuery } from './useExperimentalInvitePermissionsQuery';

import type {
	ShareAndRestrictDialogInviteCapabilitiesQuery as ShareAndRestrictDialogInviteCapabilitiesQueryType,
	ShareAndRestrictDialogInviteCapabilitiesQueryVariables,
} from './__types__/ShareAndRestrictDialogInviteCapabilitiesQuery';

const useInviteCapabilityShareAndRestrictDialogQuery =
	// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
	gql`
		query useInviteCapabilityShareAndRestrictDialogQuery($resourceARI: String!) @experimental {
			experimentalInvitePermissions(resourceARI: $resourceARI) {
				directInviteMode
				directInviteDomains
				invitePendingApprovalMode
			}
		}
	`;

export const useInviteCapability = () => {
	const { cloudId: siteId } = useSessionData();
	const resourceARI = PlatformSiteAri.create({ siteId }).toString();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onFetchCapabilitiesCompleted = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'success',
				actionSubject: 'useInviteCapabilityShareAndRestrictDialogQuery',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const onFetchCapabilitiesError = useCallback(
		(queryError: ApolloError) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'error',
					actionSubject: 'useInviteCapabilityShareAndRestrictDialogQuery',
					attributes: {
						error: queryError.message,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const inviteCapabilitiesArgs = {
		variables: { resourceARI },
		onCompleted: onFetchCapabilitiesCompleted,
		onError: onFetchCapabilitiesError,
	};

	const [loadInviteCapabilities, { data, loading, error, called }] = expValEquals(
		'confluence_deprecate_experimental_schema_exp_1',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalInvitePermissionsQuery(inviteCapabilitiesArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useLazyQueryInterop<
				ShareAndRestrictDialogInviteCapabilitiesQueryType,
				ShareAndRestrictDialogInviteCapabilitiesQueryVariables
			>(useInviteCapabilityShareAndRestrictDialogQuery, inviteCapabilitiesArgs);

	const canUserInvite = useCallback(() => {
		if (!data || loading || error) {
			return false;
		}
		return data.experimentalInvitePermissions?.invitePendingApprovalMode === 'ANYONE';
	}, [data, loading, error]);

	return {
		loadInviteCapabilities,
		canUserInvite,
		called,
	};
};
